import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi';
import { z } from 'zod';

// src/common/openapi.ts
extendZodWithOpenApi(z);
z.config({
  customError: (issue) => {
    switch (issue.code) {
      case "invalid_type":
        return "This field is required";
      case "too_small":
        if (issue.origin === "string") {
          return issue.minimum === 1 ? "This field is required" : `Enter at least ${issue.minimum} characters`;
        }
        if (issue.origin === "number") return `Enter a value of at least ${issue.minimum}`;
        if (issue.origin === "array") return "Select at least one option";
        return void 0;
      case "too_big":
        if (issue.origin === "string") return `Enter at most ${issue.maximum} characters`;
        if (issue.origin === "number") return `Enter a value of at most ${issue.maximum}`;
        return void 0;
      case "invalid_format":
        if (issue.format === "uuid") return "Select an option";
        if (issue.format === "email") return "Enter a valid email address";
        if (issue.format === "url") return "Enter a valid URL";
        return "Enter a valid value";
      default:
        return void 0;
    }
  }
});

// src/common/index.ts
var uuidSchema = z.uuid("Select an option").openapi("Uuid");
z.coerce.number().int().positive().openapi("Id");
var slugSchema = z.string().trim().min(1, "Enter a slug").max(64).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Use lowercase letters, numbers and hyphens only").openapi("Slug");
var timestampSchema = z.iso.datetime({ offset: true }).openapi("Timestamp");
z.iso.date().openapi("DateOnly");
z.email("Enter a valid email address").max(254).openapi("Email");
z.string().trim().regex(/^\+?[0-9]{7,15}$/, "Enter a valid phone number").openapi("Phone");
z.string().trim().regex(/^[A-Za-z0-9]{4,10}$/, "Enter a valid pincode").openapi("Pincode");
z.string().trim().regex(/^[A-Z]{2}$/, "Enter a valid country code").openapi("CountryCode");
z.string().trim().regex(/^[A-Z]{3}$/).openapi("CurrencyCode");
var urlSchema = z.url().max(2048).openapi("Url");
var moneySchema = z.string().regex(/^\d{1,10}\.\d{2}$/).openapi("Money");
z.string().regex(/^-?\d{1,10}\.\d{2}$/).openapi("SignedMoney");
var percentSchema = z.number().min(0).max(100).openapi("Percent");
z.number().int().min(0).openapi("Quantity");
z.string().trim().min(8).max(128).openapi("IdempotencyKey");
var requestIdSchema = z.string().trim().min(1).max(64).openapi("RequestId");
var ErrorCode = {
  VALIDATION_ERROR: "VALIDATION_ERROR",
  UNAUTHORIZED: "UNAUTHORIZED",
  FORBIDDEN: "FORBIDDEN",
  NOT_FOUND: "NOT_FOUND",
  CONFLICT: "CONFLICT",
  RATE_LIMITED: "RATE_LIMITED",
  PAYMENT_FAILED: "PAYMENT_FAILED",
  INTERNAL_ERROR: "INTERNAL_ERROR"
};
var errorCodeSchema = z.enum(ErrorCode).openapi("ErrorCode");
var errorDetailSchema = z.object({ path: z.string(), message: z.string() }).openapi("ErrorDetail");
var errorBodySchema = z.object({
  code: errorCodeSchema,
  message: z.string(),
  details: z.array(errorDetailSchema).optional(),
  requestId: requestIdSchema
}).openapi("ErrorBody");
z.object({ error: errorBodySchema }).openapi("ErrorResponse");
var MAX_PAGE_SIZE = 100;
z.object({
  page: z.number().int().min(1),
  limit: z.number().int().min(1).max(MAX_PAGE_SIZE),
  total: z.number().int().min(0),
  totalPages: z.number().int().min(0)
}).openapi("PaginationMeta");
var paginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(20)
}).openapi("PaginationQuery");
var sortOrderSchema = z.enum(["asc", "desc"]).openapi("SortOrder");
var sortQuerySchema = z.object({
  sort: z.string().trim().min(1).max(64).optional(),
  order: sortOrderSchema.default("desc")
}).openapi("SortQuery");
var searchQuerySchema = z.object({ search: z.string().trim().min(1).max(160).optional() }).openapi("SearchQuery");
var listQuerySchema = paginationQuerySchema.extend(sortQuerySchema.shape).extend(searchQuerySchema.shape).openapi("ListQuery");
z.object({ dateFrom: timestampSchema.optional(), dateTo: timestampSchema.optional() }).openapi("DateRangeQuery");
var booleanQuerySchema = z.union([z.boolean(), z.stringbool()]).openapi("BooleanQuery");
z.object({ id: uuidSchema }).openapi("IdParam");
z.object({ slug: slugSchema }).openapi("SlugParam");
z.object({ ids: z.array(uuidSchema).min(1).max(100) }).openapi("BulkIds");
z.object({ id: uuidSchema, name: z.string(), role: z.string() }).openapi("Actor");
z.object({ data: z.object({ success: z.literal(true) }) }).openapi("SuccessResponse");
z.object({ data: z.object({ count: z.number().int().min(0) }) }).openapi("CountResponse");

// src/promotions/index.ts
var CouponType = {
  PERCENTAGE: "percentage",
  FIXED: "fixed",
  FREE_SHIPPING: "free_shipping"
};
var couponTypeSchema = z.enum(CouponType).openapi("CouponType");
var CouponStatus = {
  SCHEDULED: "scheduled",
  ACTIVE: "active",
  EXPIRED: "expired",
  EXHAUSTED: "exhausted",
  DISABLED: "disabled"
};
var couponStatusSchema = z.enum(CouponStatus).openapi("CouponStatus");
var CouponRejectionReason = {
  NOT_FOUND: "not_found",
  EXPIRED: "expired",
  NOT_STARTED: "not_started",
  DISABLED: "disabled",
  USAGE_LIMIT_REACHED: "usage_limit_reached",
  USER_LIMIT_REACHED: "user_limit_reached",
  MIN_ORDER_NOT_MET: "min_order_not_met",
  NOT_APPLICABLE: "not_applicable"
};
var couponRejectionReasonSchema = z.enum(CouponRejectionReason).openapi("CouponRejectionReason");
var couponCodeSchema = z.string().trim().toUpperCase().min(3).max(32).regex(/^[A-Z0-9_-]+$/).openapi("CouponCode");
var couponScopeSchema = z.object({
  categoryIds: z.array(uuidSchema).max(100),
  brandIds: z.array(uuidSchema).max(100),
  productIds: z.array(uuidSchema).max(500)
}).openapi("CouponScope");
var couponSchema = z.object({
  id: uuidSchema,
  code: couponCodeSchema,
  description: z.string().nullable(),
  type: couponTypeSchema,
  value: moneySchema,
  percentValue: percentSchema.nullable(),
  minOrderValue: moneySchema.nullable(),
  maxDiscount: moneySchema.nullable(),
  usageLimit: z.number().int().positive().nullable(),
  perUserLimit: z.number().int().positive().nullable(),
  usedCount: z.number().int().min(0),
  startsAt: timestampSchema,
  endsAt: timestampSchema.nullable(),
  isActive: z.boolean(),
  status: couponStatusSchema,
  scope: couponScopeSchema,
  createdAt: timestampSchema,
  updatedAt: timestampSchema
}).openapi("Coupon");
var couponCreateSchema = z.object({
  code: couponCodeSchema,
  description: z.string().trim().max(500).nullable().optional(),
  type: couponTypeSchema,
  value: moneySchema.optional(),
  percentValue: percentSchema.optional(),
  minOrderValue: moneySchema.nullable().optional(),
  maxDiscount: moneySchema.nullable().optional(),
  usageLimit: z.number().int().positive().nullable().optional(),
  perUserLimit: z.number().int().positive().nullable().optional(),
  startsAt: timestampSchema,
  endsAt: timestampSchema.nullable().optional(),
  isActive: z.boolean().default(true),
  scope: couponScopeSchema.default({ categoryIds: [], brandIds: [], productIds: [] })
}).refine((v) => v.type !== "percentage" || v.percentValue != null, {
  path: ["percentValue"],
  message: "percentValue is required for a percentage coupon"
}).refine((v) => v.type !== "fixed" || v.value != null, {
  path: ["value"],
  message: "value is required for a fixed coupon"
}).openapi("CouponCreate");
var couponUpdateSchema = z.object({
  description: z.string().trim().max(500).nullable(),
  value: moneySchema,
  percentValue: percentSchema,
  minOrderValue: moneySchema.nullable(),
  maxDiscount: moneySchema.nullable(),
  usageLimit: z.number().int().positive().nullable(),
  perUserLimit: z.number().int().positive().nullable(),
  startsAt: timestampSchema,
  endsAt: timestampSchema.nullable(),
  isActive: z.boolean(),
  scope: couponScopeSchema
}).partial().openapi("CouponUpdate");
var couponQuerySchema = listQuerySchema.extend({
  type: couponTypeSchema.optional(),
  status: couponStatusSchema.optional(),
  isActive: booleanQuerySchema.optional(),
  activeOn: timestampSchema.optional()
}).openapi("CouponQuery");
var couponRedemptionSchema = z.object({
  id: uuidSchema,
  couponId: uuidSchema,
  code: couponCodeSchema,
  userId: uuidSchema,
  userName: z.string(),
  orderId: uuidSchema,
  orderNumber: z.string(),
  discountAmount: moneySchema,
  redeemedAt: timestampSchema
}).openapi("CouponRedemption");
var couponRedemptionQuerySchema = listQuerySchema.extend({
  couponId: uuidSchema.optional(),
  userId: uuidSchema.optional(),
  dateFrom: timestampSchema.optional(),
  dateTo: timestampSchema.optional()
}).openapi("CouponRedemptionQuery");
var couponValidateRequestSchema = z.object({ code: couponCodeSchema, cartId: uuidSchema.optional() }).openapi("CouponValidateRequest");
var couponValidationSchema = z.object({
  valid: z.boolean(),
  code: couponCodeSchema,
  type: couponTypeSchema.nullable(),
  discount: moneySchema,
  freeShipping: z.boolean(),
  reason: couponRejectionReasonSchema.nullable(),
  message: z.string().nullable()
}).openapi("CouponValidation");
var couponAppliedSchema = z.object({
  id: uuidSchema,
  code: couponCodeSchema,
  type: couponTypeSchema,
  discount: moneySchema,
  freeShipping: z.boolean()
}).openapi("CouponApplied");
var HomepageBannerStatus = { ACTIVE: "active", INACTIVE: "inactive" };
var homepageBannerStatusSchema = z.enum(HomepageBannerStatus).openapi("HomepageBannerStatus");
var homepageBannerSchema = z.object({
  id: uuidSchema,
  title: z.string(),
  imageUrl: urlSchema,
  linkUrl: urlSchema.nullable(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
  startsAt: timestampSchema.nullable(),
  endsAt: timestampSchema.nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema
}).openapi("HomepageBanner");
var homepageBannerCreateSchema = z.object({
  title: z.string().trim().min(1).max(120),
  imageUrl: urlSchema,
  linkUrl: urlSchema.nullable().optional(),
  sortOrder: z.number().int().min(0).default(0),
  isActive: z.boolean().default(true),
  startsAt: timestampSchema.nullable().optional(),
  endsAt: timestampSchema.nullable().optional()
}).openapi("HomepageBannerCreate");
var homepageBannerUpdateSchema = homepageBannerCreateSchema.partial().openapi("HomepageBannerUpdate");
var homepageBannerQuerySchema = listQuerySchema.extend({ isActive: booleanQuerySchema.optional() }).openapi("HomepageBannerQuery");

export { CouponRejectionReason, CouponStatus, CouponType, HomepageBannerStatus, couponAppliedSchema, couponCodeSchema, couponCreateSchema, couponQuerySchema, couponRedemptionQuerySchema, couponRedemptionSchema, couponRejectionReasonSchema, couponSchema, couponScopeSchema, couponStatusSchema, couponTypeSchema, couponUpdateSchema, couponValidateRequestSchema, couponValidationSchema, homepageBannerCreateSchema, homepageBannerQuerySchema, homepageBannerSchema, homepageBannerStatusSchema, homepageBannerUpdateSchema };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map